import { Flex } from "antd";
import { EditableTemplate } from "./Template/EditableTemplate";
import { StoryboardMenu } from "./StoryboardMenu";
import { Chapter, TabKeys, TemplateType } from "../types";
import { Fragment, useMemo, useState } from "react";
import { useTemplateEditorStore } from "../store/useTemplateEditorStore";
import { useTemplateData } from "../hooks/useTemplateData";
import { useTemplatesStatusStore } from "../store/useTemplatesStatusStore";
import { PreviewSizes } from "../hooks/usePreviewResize";
import { TemplatePreview } from "./Template/TemplatePreview";
import {
  getChapterHasTransition,
  getClosingEnabled,
  getFirstSentence,
  getIntroEnabled,
} from "../helpers";
import {
  ResizableScenesLayout,
  SlideSnapPage,
  StyledScrollableFlex,
  TemplateArea,
} from "./ResizableScenesLayout";
import { parseScript } from "pages/media/helpers/scriptAnchors";
import { useStoryboardEditing } from "../hooks/useStoryboardEditing";
import { ScrollObservedPreview } from "./ScrollObservedPreview";
import { ScrollActivateProvider } from "../hooks/useScrollActivate";
import { useScenesFit } from "../hooks/useScenesFit";
import { SNAP_PADDING_TOP } from "../constants";

export const StoryboardScenes = ({
  handleCardClick,
  previewSizes,
}: {
  handleCardClick: (chapter: Chapter) => void;
  previewSizes: PreviewSizes;
}) => {
  const {
    projectId,
    organizationId,
    templatesData: { hasTransition, storyboard, primaryDocumentId },
    brandKitData: { intro, closing },
    storyboardSections,
    mediaGenerationSettingsData: {
      mediaGenerationSettings,
      markMediaOutOfDate,
    },
  } = useTemplateData();

  const { updateStoryboardSection } = useStoryboardEditing({
    projectId: projectId ?? "",
    organizationId: organizationId ?? "",
    storyboardId: storyboard?.asset_id ?? "",
    primaryDocumentId: primaryDocumentId ?? "",
    markMediaOutOfDate,
  });

  const { currentChapter, setCurrentItem } = useTemplateEditorStore(
    (store) => ({
      currentChapter: store.currentChapter,
      setCurrentItem: store.setCurrentItem,
    })
  );

  const { type: view, sectionId } = currentChapter;

  const setActiveTab = useTemplatesStatusStore((state) => state.setActiveTab);
  const zoom = useTemplatesStatusStore((state) => state.zoom);

  const [scrollEl, setScrollEl] = useState<HTMLElement | null>(null);

  const { chapters, introduction_template_id, closing_template_id } =
    mediaGenerationSettings ?? {};

  const showIntro =
    !!intro && getIntroEnabled(mediaGenerationSettings, !!intro);
  const showClosing =
    !!closing && getClosingEnabled(mediaGenerationSettings, !!closing);

  const introductionChapter = useMemo(
    () => ({
      templateId: introduction_template_id ?? undefined,
      topic: "Introduction",
      sectionId: "",
      type: TemplateType.INTRODUCTION,
    }),
    [introduction_template_id]
  );

  const closingChapter = useMemo(
    () => ({
      templateId: closing_template_id ?? undefined,
      topic: "Closing",
      sectionId: "",
      type: TemplateType.CLOSING,
    }),
    [closing_template_id]
  );

  // Scenes fill the panel: fit the largest box of the preview's aspect ratio
  // into the actual scroll viewport, mirroring the slide-based editor.
  const { previewSize: fitSize, pageHeight } = useScenesFit(
    scrollEl,
    previewSizes
  );

  // Zoom is purely visual: scale the fitted preview size so the rendered scenes
  // grow/shrink together. Item position math keys off this same size, so the
  // relative values saved in the template are unaffected.
  const previewSize = useMemo(
    () => ({
      width: Math.round(fitSize.width * zoom),
      height: Math.round(fitSize.height * zoom),
    }),
    [fitSize.width, fitSize.height, zoom]
  );

  const templateSize = useMemo(
    () => ({
      width: previewSize.width,
      height: previewSize.height,
    }),
    [previewSize.width, previewSize.height]
  );

  const templatePreviewBorder = useMemo(
    () => ({
      borderWidth: 0,
      borderRadius: 0,
    }),
    []
  );

  return (
    <ResizableScenesLayout
      menu={
        <StoryboardMenu
          previewWidth={previewSize.width}
          previewHeight={previewSize.height}
        />
      }
    >
      <StyledScrollableFlex
        ref={setScrollEl}
        className="video-editor-scene"
        vertical
        // Snap at <=100%: at and below original size a scene fits the viewport,
        // so the snap page is one viewport tall and snapping lands cleanly. Only
        // when zoomed in (>100%) is a scene taller than the viewport, leaving no
        // edge to snap to — there we let the user scroll freely.
        $snap={zoom <= 1}
        $snapPaddingTop={SNAP_PADDING_TOP}
        onMouseDown={(e) => {
          const target = e.target as HTMLElement;

          // Check if the clicked element or any parent has data-interactive
          if (!target.closest("[data-interactive]")) {
            setActiveTab(TabKeys.layout);
            setCurrentItem(undefined);
          }
        }}
      >
        <ScrollActivateProvider rootEl={scrollEl}>
          <Flex vertical gap={24}>
            {showIntro && (
              <SlideSnapPage $minHeight={pageHeight}>
                <TemplateArea>
                  {currentChapter.type === TemplateType.INTRODUCTION ? (
                    <EditableTemplate
                      previewWidth={previewSize.width}
                      previewHeight={previewSize.height}
                    />
                  ) : (
                    <ScrollObservedPreview
                      onActivate={() => handleCardClick(introductionChapter)}
                    >
                      <TemplatePreview
                        chapter={introductionChapter}
                        onCardClick={() => {
                          handleCardClick(introductionChapter);
                        }}
                        templateSize={templateSize}
                        border={templatePreviewBorder}
                      />
                    </ScrollObservedPreview>
                  )}
                </TemplateArea>
              </SlideSnapPage>
            )}
            {storyboardSections.map((section) => {
              const chapterSettings = chapters?.find(
                (chapter) => chapter.chapter_id === section.id
              );
              const { chapter_template_id, transition_template_id } =
                chapterSettings ?? {};
              const chapterHasTransition = getChapterHasTransition(
                chapterSettings,
                hasTransition
              );

              const commonChapterData = {
                topic: section.topic,
                sectionId: section.id,
              };
              const chapterData = {
                ...commonChapterData,
                templateId: chapter_template_id ?? undefined,
                type: TemplateType.CHAPTER,
              };
              const transitionData = {
                ...commonChapterData,
                templateId: transition_template_id ?? undefined,
                type: TemplateType.TRANSITION,
              };

              const handleChapterClick = () => {
                handleCardClick(chapterData);
              };
              const handleTransitionClick = () => {
                handleCardClick(transitionData);
              };

              const chapterPreview = (
                <ScrollObservedPreview onActivate={handleChapterClick}>
                  <TemplatePreview
                    chapter={chapterData}
                    previewText={getFirstSentence(
                      parseScript(section.script.content).text
                    )}
                    onCardClick={handleChapterClick}
                    templateSize={templateSize}
                    border={templatePreviewBorder}
                  />
                </ScrollObservedPreview>
              );
              const transitionPreview = (
                <ScrollObservedPreview onActivate={handleTransitionClick}>
                  <TemplatePreview
                    chapter={transitionData}
                    previewText={section.topic}
                    onCardClick={handleTransitionClick}
                    templateSize={templateSize}
                    border={templatePreviewBorder}
                  />
                </ScrollObservedPreview>
              );

              const isSelected = sectionId === section.id;

              const transitionSlot =
                isSelected && view === TemplateType.TRANSITION ? (
                  <EditableTemplate
                    previewWidth={previewSize.width}
                    previewHeight={previewSize.height}
                    updateStoryboardSection={(sectionId, updates) => {
                      updateStoryboardSection(sectionId, updates).catch(
                        (error) => {
                          console.error("Error updating section", error);
                        }
                      );
                    }}
                  />
                ) : (
                  transitionPreview
                );

              const chapterSlot =
                isSelected && view === TemplateType.CHAPTER ? (
                  <EditableTemplate
                    chapterScript={parseScript(section.script.content).text}
                    previewWidth={previewSize.width}
                    previewHeight={previewSize.height}
                  />
                ) : (
                  chapterPreview
                );

              return (
                <Fragment key={section.id}>
                  {chapterHasTransition && (
                    <SlideSnapPage $minHeight={pageHeight}>
                      <TemplateArea>{transitionSlot}</TemplateArea>
                    </SlideSnapPage>
                  )}
                  <SlideSnapPage $minHeight={pageHeight}>
                    <TemplateArea>{chapterSlot}</TemplateArea>
                  </SlideSnapPage>
                </Fragment>
              );
            })}
            {showClosing && (
              <SlideSnapPage $minHeight={pageHeight}>
                <TemplateArea>
                  {currentChapter.type === TemplateType.CLOSING ? (
                    <EditableTemplate
                      previewWidth={previewSize.width}
                      previewHeight={previewSize.height}
                    />
                  ) : (
                    <ScrollObservedPreview
                      onActivate={() => handleCardClick(closingChapter)}
                    >
                      <TemplatePreview
                        chapter={closingChapter}
                        onCardClick={() => {
                          handleCardClick(closingChapter);
                        }}
                        templateSize={templateSize}
                        border={templatePreviewBorder}
                      />
                    </ScrollObservedPreview>
                  )}
                </TemplateArea>
              </SlideSnapPage>
            )}
          </Flex>
        </ScrollActivateProvider>
      </StyledScrollableFlex>
    </ResizableScenesLayout>
  );
};
